/*
 *   File : signup.js
 *   Author : https://evoqins.com
 *   Description : Login and Signup page
 *   Integrations : js-cookie,react-toastify, @react-oauth/google,react-apple-signin-auth
 *   Version : 1.0.0
 *   Date : 22-09-2022  
*/

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
// import AppleSignin from 'react-apple-signin-auth';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut, signInWithCredential } from "firebase/auth";
import { toast } from "react-toastify";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Modal } from "bootstrap";
import Cookies from "js-cookie";

//custom components
import { auth } from "./firebase";
import { OTPInput, CustomPhoneInput, RadioGroup, CustomTextInput } from "../../Component/Form-Elements";
import { PrimaryButton } from "../../Component/Buttons";
import { Icon } from "../../Component/Icon"
import { KycStatusModal } from "../../Component/Modal";
import { Header } from "../../Component/Navigation";
import { Footer } from "../../Component/Footer";
import { AuthSlider } from "../../Component/Banner";
import { _getProfile, _getSignupProgress, _trackEvent } from "../../Helper";
import APIService from "../../Service/service";
import Store from "../../Store/store";
import { _parseJwtToken, APPLE_AUTH_CONFIG } from "../../Helper/function";

//custom styles
import styles from "../../Style/Component/login.module.scss";
import Colors from "../../Style/color.scss";

// Assets
import Inactive from "../../Assets/Images/check-box.svg";
import ActiveCheckbox from "../../Assets/Images/check-box-active.svg";

// Custom data
const CITIZEN_SELECT = [
    {
        id: true,
        display_name: "Yes"
    },
    {
        id: false,
        display_name: "No"
    },
];


const SignUp = (props) => {

    const inputRef = useRef(null);
    const screenViewLatch = useRef(false);
    const otpViewLatch = useRef(false);
    const navigate = useNavigate();
    const { state } = useLocation();

    const [currentStep, setCurrentStep] = useState(1); // 1 => user details, 2 => OTP verification    
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [phoneNumber, setPhoneNumber] = useState(state && state.phone_number ? state.phone_number : "");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [timer, setTimer] = useState(0);
    const [loading, setLoading] = useState(false);
    const [disableResendOTP, setDisableResendOTP] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState(state && state.country_code ? state.country_code : "+91");
    const [selectedCountryLocale, setSelectedCountryLocale] = useState(state && state.country_locale ? state.country_locale : "IN");
    const [isSocialSignUp, setIsSocialSignUp] = useState(false);

    const [isUserNRI, setIsUserNRI] = useState(false);
    const [selectTerms, setSelectTerms] = useState(true);

    // OTP States
    const mobileOTPInputRef = useRef(null);
    const emailOTPInputRef = useRef(null);
    const [mobileOTP, setMobileOTP] = useState("");
    const [emailOTP, setEmailOTP] = useState("");
    const [focusMobileOTPInput, setFocusMobileOTPInput] = useState(2);
    const [focusEmailOTPInput, setFocusEmailOTPInput] = useState(0);

    const [userFlow, setUserFlow] = useState("SIGNUP");
    const [statusModal, setStatusModal] = useState(false);
    const [isKYCRejected, setIsKYCRejected] = useState(false);
    const [googleLoginResponse, setGoogleLoginResponse] = useState({});

    // Showing NRI info 
    useEffect(() => {
        if (selectedCountryCode !== "+91") {
            setIsUserNRI(true);
        }
    }, [selectedCountryCode]);

    useEffect(()=>{
        console.log('userFlow', userFlow);
    },[userFlow])

    //for timer
    useEffect(() => {
        if (timer > 0) {
            // Start the timer when requestOtpSuccess becomes true
            const countdown = setInterval(() => {
                setTimer((prevTimer) => prevTimer - 1);
            }, 1000);
            // Clear the timer when the component unmounts or when the timer reaches 0
            return () => {
                clearInterval(countdown);
            };
        };
    }, [timer]);

    useEffect(() => {
        if (statusModal === true) {
            let kyc_status = new Modal(document.getElementById('kyc-status-modal'));
            kyc_status.show();
        }
    }, [statusModal]);

    // Fire signup details screen view once on mount
    useEffect(() => {
        if (!screenViewLatch.current) {
            screenViewLatch.current = true;
            _trackEvent("screen_signup_details_viewed", { screen: "signup" });
        }
    }, []);

    // Fire OTP verification screen view once when entering step 2
    useEffect(() => {
        if (currentStep === 2 && !otpViewLatch.current) {
            otpViewLatch.current = true;
            _trackEvent("screen_otp_verification_viewed", { screen: "signup" });
        }
    }, [currentStep]);

    // When redirecting from signup flow
    useEffect(() => {
        if (state !== null) {
            if (state.phone_number) {
                setPhoneNumber(state.phone_number);
            }

            if (state.email) {
                setEmail(state.email);
            }

            if (state.country_code) {
                setSelectedCountryCode(state.country_code);
            }

            if (state.country_locale) {
                setSelectedCountryLocale(state.country_locale);
            }

        }
    }, [state]);

    // handling Enter key 
    useEffect(() => {

        const _handleOnEnter = (e) => {
            if (e.key === 'Enter') {
                if (currentStep == 1) {
                    _handleGetSignInOtp();
                } else {
                    _handleVerifyOtp();
                }
            }
        };

        document.addEventListener('keydown', _handleOnEnter);

        return () => {
            document.removeEventListener('keydown', _handleOnEnter);
        };

    }, [currentStep, phoneNumber, mobileOTP, emailOTP, email]);

    //handle citizenship select
    const _handleCitizenSelect = (id) => {
        _trackEvent("nri_selection_clicked", { screen: "signup", is_nri: id });
        setIsUserNRI(id);
    };

    //handle select terms and condition
    const _handleSelectTerms = () => {
        setSelectTerms((selectTerms) => !selectTerms);
    };


    //handle email change
    const _handleEmailChange = (value) => {
        if (!email && value) {
            _trackEvent("email_entered", { screen: "signup" });
        }
        setEmail(value.toLowerCase());
        setEmailError("");
    };

    // handle phone number change
    const _handlePhoneNumberChange = (value, selected_country_code, country_locale) => {
        if (!phoneNumber && value) {
            _trackEvent("mobile_number_entered", { screen: "signup" });
        }
        setPhoneNumber(value || "");
        setPhoneNumberError("");

        if (selected_country_code) {
            setSelectedCountryCode(selected_country_code);
        }

        if (country_locale) {
            setSelectedCountryLocale(country_locale);
        }
    };

    //handle back
    const _handleBack = () => {
        setCurrentStep((cur_step) => cur_step - 1);
        setUserFlow("SIGNUP");
    };

    // handle RESEND OTP 
    const _handleResendOTP = () => {
        let resend = true;
        _trackEvent("otp_resend_clicked", { screen: "signup" });
        _getOTP(resend);
        setDisableResendOTP(true);
    };

    //handle change mobile otp
    const _handleChangeMobileOTP = (value) => {
        setMobileOTP(value);
        if (value.length == 4) {
            setFocusEmailOTPInput(2);
        }
    };

    //handle submit otp
    const _handleChangeEmailOTP = (value) => {
        setEmailOTP(value)
    };

    //handle get otp signup
    const _handleGetSignInOtp = () => {

        let validation = true;

        // valid phone number and email pattern 
        let valid_phone = /^[6-9]\d{9}$/;
        let validate_email = /^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/;
        // removing country code
        let phone_number = Number(phoneNumber?.replace(selectedCountryCode, ""));

        // Validating phone
        if (selectedCountryCode == "+91") {
            if (!valid_phone.test(phone_number)) {
                setPhoneNumberError("Please enter a valid 10-digit phone number");
                validation = false;
            }
        } else {
            let is_valid_number = isValidPhoneNumber(String(phoneNumber));
            if (is_valid_number === false) {
                setPhoneNumberError("Please enter a valid number");
                validation = false;
            }
        }

        // Validating email
        if (!validate_email.test(email)) {
            setEmailError("Please enter a valid email id");
            validation = false;
        }



        if (validation === true) {
            setLoading(true);
            _trackEvent("get_otp_clicked", { screen: "signup" });
            _trackEvent("otp_requested", { screen: "signup" });
            _getOTP(); // API call to verify and sent OTP
        }
    };

    //handle for login otp verify
    const _handleVerifyOtp = () => {
        setLoading(true);
        _trackEvent("otp_verification_submitted", { screen: "signup" });
        // API call
        _verifyOTP();
    };

    // handle modal close
    const _handleModalClose = () => {
        setStatusModal(false);
        if (isKYCRejected) {
            // Updating store to navigate to onboarding flow
            Store.dispatch({
                type: 'UPDATE_ONBOARDING_TOKEN',
                payload: {
                    refresh_token: Cookies.get('onboarding_refresh_token'),
                    access_token: sessionStorage.getItem('access_token')
                }
            });
        }
    };

    // handle google login  

    const _handleGoogleLogin = async () => {
        _trackEvent("google_signup_clicked", { screen: "signup", method: "google" });
        try {
            await signOut(auth); // 👈 clear previous session

            const provider = new GoogleAuthProvider();
            provider.addScope("email");
            provider.addScope("profile");

            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const idToken = await user.getIdToken();

            console.log("Firebase ID Token:", idToken);

            const url = 'auth/google-signin';
            let payload = {
                idToken,
                user,
            }
            const api_version = 2
            // Send token to backend
            APIService(true, url, payload, null, api_version).then((response) => {
                if (response.status_code === 200) {
                    _trackEvent("google_signup_success", { screen: "signup", method: "google" });
                    console.log('response.data', response);

                    if(response.data?.flow === "LOGIN") {
                        toast.dismiss();
                        toast.info("Account already exists, please login to continue", {
                        className: "e-toast",
                        });
                         navigate("/", {
                            state : {
                                email : user.email,
                            }
                        });
                    } else {
                        toast.success('success');
                        setIsSocialSignUp(true);
                        setEmail(user.email);
                        setGoogleLoginResponse(response.data);
                    }

                   
                } else {
                    toast.error(response.message)
                }
            })

        } catch (error) {
            console.error("Google sign-in error:", error);
        }
    };

    // const _handleAppleLoginSuccess = async (response) => {
    //     try {
    //         console.log("Apple response:", response);

    //         toast.dismiss("");
    //         toast.info("Signing in...", { className: "e-toast" });

    //         const appleIdToken = response?.authorization?.id_token;

    //         if (!appleIdToken) {
    //             toast.error("Apple login failed: missing token");
    //             return;
    //         }

    //         const provider = new OAuthProvider("apple.com");

    //         // Create Firebase credential (NO POPUPS)
    //         const credential = provider.credential({
    //             idToken: appleIdToken,
    //         });

    //         // Firebase login
    //         const result = await signInWithCredential(auth, credential);
    //         const user = result.user;

    //         const firebaseIdToken = await user.getIdToken();

    //         const payload = {
    //             idToken: firebaseIdToken,
    //             user: {
    //                 uid: user.uid,
    //                 name: user.displayName,
    //                 email: user.email,
    //             }
    //         };

    //         // Backend API call
    //         APIService(true, "auth/google-signin", payload, null, 2)
    //             .then((res) => {
    //                 if (res.status_code === 200) {
    //                     toast.success("Login success 🎉");
    //                 } else {
    //                     toast.error(res.message);
    //                 }
    //             });

    //     } catch (error) {
    //         console.error("Apple login error:", error);
    //         toast.error("Something went wrong");
    //     }
    // };


    const _handleAppleLogin = async () => {
        _trackEvent("apple_signup_clicked", { screen: "signup", method: "apple" });
        try {
            await signOut(auth);

            const provider = new OAuthProvider("apple.com");
            provider.addScope("email");
            provider.addScope("name");

            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            const firebaseIdToken = await user.getIdToken();
            const appleIdToken = result?._tokenResponse?.oauthIdToken || null;
            const scopes = result?._tokenResponse?.oauthScope?.split(" ") || [];
            const serverAuthCode = result?._tokenResponse?.authorizationCode || null;

            const payload = {
                idToken: firebaseIdToken,
                user: {
                    uid: user.uid,
                    name: user.displayName,
                    email: user.email,
                },
                scopes,
                serverAuthCode,
            };

            console.log("Apple Login Payload:", payload);

            await fetch("/api/auth/login", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            _trackEvent("apple_signup_success", { screen: "signup", method: "apple" });

        } catch (error) {
            console.error("Apple sign-in error:", error);
        }
    };



    const _handleGoogleLoginSuccess = (access_token) => {

        if (access_token) {
            fetch(`https://www.googleapis.com/oauth2/v3/userinfo?access_token=${access_token}`)
                .then(response => response.json())
                .then(data => {
                    console.log(data);
                    toast.dismiss("");
                    toast.info("Test Mode : Login successful ", {
                        className: "e-toast",
                    });
                })
                .catch(error => console.error(error));
        } else {
            toast.dismiss("");
            toast.error("Something went wrong, please try again later", {
                className: "e-toast",
            });
        }
    };



    const _handleAppleLoginError = (error) => {
        console.error('Apple Sign-In Error:', error);
    };


    // ================================ //
    //============= API CALLS ========= //

    // API - get sign-in otp
    const _getOTP = (resend) => {

        let api_version = 2;
        let is_post = true;
        let url = 'auth/get-signin-otp';
        let data = {
            "country_code": selectedCountryCode,
            "phone_number": Number(phoneNumber.replace(selectedCountryCode, "")),
            "email": email,
            "type": resend === true ? "RESEND" : null,
            "is_nri": isUserNRI
        };


        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                _trackEvent("otp_sent", { screen: "signup" });
                if (isUserNRI && isSocialSignUp) {
                    const { refresh_token, access_token, signup_progress_text, is_auth_configured } = googleLoginResponse;

                    if (signup_progress_text === "INVESTED") {

                        Store.dispatch({
                            type: 'UPDATE_REFRESH_TOKEN',
                            payload: response.data.refresh_token
                        });

                        Cookies.set('refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });

                        toast.dismiss();
                        toast.success("OTP verified successfully", {
                            className: "e-toast",
                        });

                        navigate("/unlock", {
                            replace: true
                        });


                    } else if (signup_progress_text === "KYC_SUBMITTED") {
                        setStatusModal(true); // KYC status modal    
                    } else if (signup_progress_text === "KYC_REJECTED") {
                        setStatusModal(true); // KYC status modal   
                        setIsKYCRejected(true);
                        Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                        sessionStorage.setItem('access_token', access_token);
                    } else {
                        Store.dispatch({
                            type: 'UPDATE_ONBOARDING_TOKEN',
                            payload:
                                { refresh_token, access_token }
                        });
                        Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                        sessionStorage.setItem('access_token', access_token);
                        navigate("/onboarding", {
                            state: {
                                is_nri: isUserNRI
                            }
                        });

                        if (signup_progress_text === "ACCOUNT_CREATED") {
                            let message = is_auth_configured ? "Please make first investment to continue" : "Please set PIN to continue";
                            toast.info(message, {
                                className: "e-toast",
                            });

                        } else {
                            toast.dismiss();
                            toast.success("OTP verified successfully", {
                                className: "e-toast",
                            });
                        }

                    }
                    localStorage.setItem("is_auth_configured", is_auth_configured);
                }

                setCurrentStep(2);
                setMobileOTP("");
                setEmailOTP("");
                setTimer(30);
                setUserFlow(response.data?.flow);
                console.log('response.data?.flow', response.data?.flow);

                if (response.data.mobile_otp) {
                    setFocusMobileOTPInput(2);
                } else {
                    setFocusEmailOTPInput(2);
                }

                if (resend === true) {
                    toast.dismiss();
                    toast.success("OTP sent successfully", {
                        className: "e-toast",
                    });
                }

            } else {
                if (response?.data?.flow === "LOGIN") {

                    _trackEvent("existing_user_detected", { screen: "signup", flow: "LOGIN" });
                    _trackEvent("signin_clicked", { screen: "signup" });
                    setUserFlow("LOGIN");
                    console.log("response?.data?.flow ---", response?.data?.flow );

                    let is_indian = selectedCountryCode === "+91" ;

                    navigate("/", {
                        state : {
                            phone_number : is_indian ? phoneNumber : null,
                            email : is_indian === false &&  email,
                            country_code :is_indian  && selectedCountryCode,
                            country_locale : is_indian && selectedCountryLocale
                        }
                    });

                    toast.dismiss();
                    toast.info("Account already exists, please login to continue", {
                     className: "e-toast",
                    });

                    // setCurrentStep(2);
                    // setMobileOTP("");
                    // setEmailOTP("");
                    // setTimer(30);

                    // if (selectedCountryCode === "+91") {
                    //     setFocusMobileOTPInput(2);
                    // } else {
                    //     setFocusEmailOTPInput(2);
                    // }



                    // if (resend === true) {
                    //     toast.dismiss();
                    //     toast.success("OTP sent successfully", {
                    //         className: "e-toast",
                    //     });
                    // }



                } else {
                    _trackEvent("otp_send_failed", { screen: "signup", status_code: response.status_code, error_message: response.message });
                    toast.dismiss();
                    toast.error(response.message, {
                        type: "error",
                        className: "e-toast",
                    });
                }

            }
            setLoading(false);
            setDisableResendOTP(false);
        });
    };

    // API - Verify otp
    const _verifyOTP = () => {

        setLoading(true);

        let api_version = 2;
        let is_post = true;
        let url = 'auth/verify-otp';
        let data = {
            "type": userFlow === "LOGIN" ? "LOGIN" : "SIGNUP",
        };

        if (selectedCountryCode === "+91") {
            data = {
                ...data,
                "country_code": selectedCountryCode,
                "phone_number": Number(phoneNumber.replace(selectedCountryCode, "")),
                "phone_otp": mobileOTP,
                "email": email,
                "email_otp": emailOTP,
            }
        } else {
            data = {
                ...data,
                "email": email,
                "email_otp": emailOTP,
            }
        };

        APIService(is_post, url, data, null, api_version).then((response) => {
            if (response.status_code === 200) {
                _trackEvent("otp_verified", { screen: "signup" });
                _trackEvent("otp_verification_success", { screen: "signup" });
                const { refresh_token, access_token, signup_progress_text, is_auth_configured } = response.data;

                if (signup_progress_text === "INVESTED") {

                    Store.dispatch({
                        type: 'UPDATE_REFRESH_TOKEN',
                        payload: response.data.refresh_token
                    });

                    Cookies.set('refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });

                    toast.dismiss();
                    toast.success("OTP verified successfully", {
                        className: "e-toast",
                    });

                    navigate("/unlock", {
                        replace: true
                    });


                } else if (signup_progress_text === "KYC_SUBMITTED") {
                    setStatusModal(true); // KYC status modal    
                } else if (signup_progress_text === "KYC_REJECTED") {
                    setStatusModal(true); // KYC status modal   
                    setIsKYCRejected(true);
                    Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                    sessionStorage.setItem('access_token', access_token);
                } else {
                    Store.dispatch({
                        type: 'UPDATE_ONBOARDING_TOKEN',
                        payload:
                            { refresh_token, access_token }
                    });
                    Cookies.set('onboarding_refresh_token', refresh_token, { domain: process.env.REACT_APP_DOMAIN, path: '/', expires: 30 });
                    sessionStorage.setItem('access_token', access_token);
                    navigate("/onboarding", {
                        state: {
                            is_nri: isUserNRI
                        }
                    });

                    if (signup_progress_text === "ACCOUNT_CREATED") {
                        let message = is_auth_configured ? "Please make first investment to continue" : "Please set PIN to continue";
                        toast.info(message, {
                            className: "e-toast",
                        });

                    } else {
                        toast.dismiss();
                        toast.success("OTP verified successfully", {
                            className: "e-toast",
                        });
                    }

                }
                localStorage.setItem("is_auth_configured", is_auth_configured);
            } else {
                _trackEvent("otp_verification_failed", { screen: "signup", status_code: response.status_code, error_message: response.message });
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });

                setMobileOTP("");
                setEmailOTP("");
            }

            setLoading(false);
        });
    };

    return (
        <section className="e-login-gradient">
            <Header />
            <div className="col-sm-10 col-md-10 col-lg-8 col-xl-7 mx-auto px-md-0">
                <div className="e-padding-block-40px-res padding-76px-top padding-150px-bottom position-relative">
                    <div className={`${styles.e_onboarding_wrapper} e-border-radius-24`}>
                        <div className="row justify-content-between ">
                            <AuthSlider />
                            <div className="col-md-6  col-12 position-relative gradient-vr-line">

                                {
                                    // step-1 =>  Login  method select
                                    currentStep === 1 ?
                                        <div className="margin-36px-right no-margin-res-md">
                                            {
                                                isSocialSignUp === false ?
                                                    <h2 className="text-gradient-primary e-alt-font-poppins e-font-weight-600 e-font-18-res e-font-24 line-height-32px letter-spacing-neg-2per mb-1">
                                                        Let’s Confirm
                                                    </h2>
                                                    :
                                                    <h2 className="text-gradient-primary e-alt-font-poppins e-font-weight-600 e-font-18-res e-font-24 line-height-32px letter-spacing-neg-2per mb-1">
                                                        One more step
                                                    </h2>
                                            }

                                            {
                                                isSocialSignUp ?
                                                    <p className="e-text-onyx-80per e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                                        Enter your Aadhaar-linked mobile number.
                                                    </p>
                                                    :
                                                    <p className="e-text-onyx-80per e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                                        Enter your Aadhaar-linked mobile number and email.
                                                    </p>
                                            }


                                            <div className="mb-4">
                                                <h6 className='margin-12px-bottom e-text-charleston-green e-font-14 e-alt-font-poppins e-font-weight-400'>
                                                    Are you an NRI Investor*
                                                </h6>
                                                <div className='d-flex flex-wrap gap-3'>
                                                    <RadioGroup data={CITIZEN_SELECT}
                                                        selected={isUserNRI}
                                                        handleSelect={_handleCitizenSelect} />
                                                </div>

                                                {/* Info message */}
                                                {
                                                    isUserNRI &&
                                                    <div className='d-flex align-items-start gap-2 e-bg-cornsilk e-border-radius-16 p-2 mt-3'>
                                                        {/* <Icon icon="info"
                                                    size={"0.75rem"}
                                                    color={Colors.ochre} /> */}
                                                        <span className='e-text-ochre e-alt-font-poppins e-font-14 e-font-weight-400 line-height-24px'>
                                                            As an NRI investor, verification will be done via your email address. You’ll receive an OTP on your registered email. Your phone number will be added without OTP verification.                                                </span>
                                                    </div>
                                                }
                                            </div>

                                            <div className="pt-1 mb-4">
                                                <CustomPhoneInput label="Mobile Number*"
                                                    id="login-phone-input"
                                                    placeholder="9876543210"
                                                    className={phoneNumber ? `e-number-input-padding w-100 ` : `e-number-input-padding w-100`}
                                                    type="text"
                                                    floatingLabel
                                                    value={phoneNumber}
                                                    selectedCountryCode={selectedCountryCode}
                                                    selectedCountryLocale={selectedCountryLocale}
                                                    error={phoneNumber && phoneNumberError}
                                                    handleChange={_handlePhoneNumberChange} />
                                            </div>

                                            {
                                                isSocialSignUp === false ?
                                                    <div className='mb-4 pt-1'>
                                                        <CustomTextInput label="Email*"
                                                            placeholder="joedoe@gmail.com"
                                                            type="text"
                                                            value={email}
                                                            floatingLabel
                                                            errorMsg={emailError}
                                                            className="w-100"
                                                            handleChange={_handleEmailChange} />
                                                    </div>
                                                    : null
                                            }


                                            <PrimaryButton name={`Get OTP`}
                                                loading={loading}
                                                disabled={loading}
                                                inActive={email?.length !== 0 && phoneNumber?.length !== 0 ? false : true}
                                                className={`w-100 padding-12px-tb e-font-16`}
                                                onPress={_handleGetSignInOtp} />

                                            {
                                                isSocialSignUp === false ?
                                                    <div className="mt-3">
                                                        <div className="d-flex align-items-center gap-18px">
                                                            <span className="gradient-border-line flex-fill transform-rotate-180"></span>
                                                            <span className="e-text-gray e-alt-font-poppins e-font-weight-500 e-font-12-res e-font-16 line-height-18px ">
                                                                Or Continue with
                                                            </span>
                                                            <span className="gradient-border-line flex-fill"></span>
                                                        </div>
                                                        <div className="mt-3 d-flex justify-content-center align-items-center gap-2">
                                                            <button className="social-login-button flex-fill" onClick={_handleGoogleLogin}>
                                                                <Icon icon="google_icon"
                                                                    className="cursor-pointer w-24px width-20px-res"
                                                                    onClick={() => { }} />
                                                                Google
                                                            </button>
                                                            {/* <button
                                                        className="social-login-button flex-fill"
                                                        onClick={_handleAppleLogin}
                                                    >
                                                        <Icon icon="apple-icon" className="cursor-pointer w-24px width-20px-res" />
                                                        Apple
                                                    </button> */}
                                                            {/* <AppleSignin
                                                        authOptions={APPLE_AUTH_CONFIG}
                                                        onSuccess={_handleAppleLoginSuccess}
                                                        onError={_handleAppleLoginError}
                                                        render={(props) => <button {...props} className="social-login-button flex-fill">
                                                            <Icon icon="apple-icon"
                                                                className="cursor-pointer w-24px width-20px-res"
                                                                onClick={() => { }} />
                                                            Apple
                                                        </button>}
                                                    /> */}
                                                        </div>
                                                    </div>
                                                    :
                                                    null
                                            }




                                        </div>
                                        :

                                        // OTP verification
                                        currentStep === 2 ?
                                            <div className="margin-32px-right no-margin-res-md">
                                                <Icon icon="arrow-back-left"
                                                    className="cursor-pointer mb-3"
                                                    size={24}
                                                    color={Colors.charleston_green}
                                                    onClick={_handleBack} />

                                                <h2 className="text-gradient-primary e-alt-font-poppins e-font-weight-600 e-font-18-res e-font-24 line-height-32px letter-spacing-neg-2per mb-1">
                                                    OTP Verification
                                                </h2>

                                                <p className="e-text-onyx-80per e-alt-font-poppins e-font-weight-400 e-font-14-res e-font-16 line-height-24px">
                                                    Enter the 4 digit OTP received on your registered
                                                    {
                                                        isSocialSignUp ?
                                                            " phone" :
                                                            selectedCountryCode === "+91" ? " phone and email" : " email"
                                                    }
                                                </p>

                                                <div className="d-flex flex-column row-gap-3">

                                                    {
                                                        selectedCountryCode === "+91" &&
                                                        <OTPInput ref={mobileOTPInputRef}
                                                            id="mobile-otp"
                                                            label="Enter Phone OTP*"
                                                            value={mobileOTP}
                                                            spellcheck="false"
                                                            focused_input={focusMobileOTPInput}
                                                            onFocus={() => setFocusMobileOTPInput(2)}
                                                            onBlur={() => setFocusMobileOTPInput(0)}
                                                            onChange={_handleChangeMobileOTP} />
                                                    }
                                                    {
                                                        isSocialSignUp === false ?
                                                            <OTPInput ref={emailOTPInputRef}
                                                                label="Enter Email OTP*"
                                                                id="email-otp"
                                                                value={emailOTP}
                                                                spellcheck="false"
                                                                focused_input={focusEmailOTPInput}
                                                                onFocus={() => setFocusEmailOTPInput(2)}
                                                                onBlur={() => setFocusEmailOTPInput(0)}
                                                                onChange={_handleChangeEmailOTP} />
                                                            :
                                                            null
                                                    }

                                                </div>

                                                <div className='e-inline-flex flex-wrap e-text-charleston-green gap-2  e-alt-font-poppins line-height-18px mt-3'>
                                                    <button className={`${timer !== 0 ? "pe-none" : ""}  text-decoration-hover-underline e-font-14-res e-font-16 e-text-egyptian-blue px-0 d-flex gap-1 border-0 e-bg-transparent e-font-weight-600`}
                                                        disabled={disableResendOTP}
                                                        onClick={_handleResendOTP} >
                                                        Resend OTP
                                                        {timer !== 0 &&
                                                            <p className='mb-0 e-text-charleston-green  e-alt-font-poppins e-font-weight-500 e-font-14'>
                                                                in {timer} {timer == 1 ? "second" : "seconds"}
                                                            </p>
                                                        }
                                                    </button>

                                                </div>

                                                <div className='mt-4'>
                                                    {console.log('isSocialSignUp', isSocialSignUp)}
                                                    {
                                                        isSocialSignUp ?
                                                            <PrimaryButton name="Confirm"
                                                                loading={loading}
                                                                disabled={loading}
                                                                inActive={selectedCountryCode === "+91" && mobileOTP.length === 4 && selectTerms ? false : true}
                                                                className={`w-100 padding-12px-tb e-font-16`}
                                                                onPress={_handleVerifyOtp} />
                                                            :
                                                            <PrimaryButton name="Confirm"
                                                                loading={loading}
                                                                disabled={loading}
                                                                inActive={selectedCountryCode === "+91" && mobileOTP.length === 4 && emailOTP.length === 4 && selectTerms ? false : emailOTP.length === 4 && selectTerms ? false : true}
                                                                className={`w-100 padding-12px-tb e-font-16`}
                                                                onPress={_handleVerifyOtp} />
                                                    }

                                                </div>

                                                {/* terms and conditions */}
                                                <div className='pt-4'>
                                                    <div className='d-flex align-items-start gap-0-12px'>
                                                        <img src={selectTerms ? ActiveCheckbox : Inactive}
                                                            draggable={false}
                                                            className='cursor-pointer'
                                                            alt='checkbox'
                                                            onClick={_handleSelectTerms} />

                                                        <div className="position-relative">
                                                            <p className='mb-0  e-text-charleston-green e-font-12 e-alt-font-poppins e-font-weight-500 line-height-18px'>
                                                                I agree to the Index Funds Sahi Hai
                                                                <a href="https://indexfundssahihai.com/terms"
                                                                    target="_blank"
                                                                    onClick={() => _trackEvent("terms_and_conditions_clicked", { screen: "signup", type: "terms" })}
                                                                    className='px-1 e-text-egyptian-blue cursor-pointer text-decoration-hover-underline'>
                                                                    terms and conditions.
                                                                </a>
                                                                Also, I assure that I will read KIM & SID of funds before investments.
                                                            </p>
                                                            <p className={`${selectTerms === false ? "opacity-100" : "opacity-0"} position-absolute top-100 start-0 padding-2px-top mb-0 e-error-message e-font-12`}>
                                                                Please select terms and conditions.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            :
                                            null
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* footer section  */}
            <div className="e-bg-anti-flash-white">
                <Footer auth={true} />
            </div>


            {/* kyc status modal  */}
            {
                statusModal === true &&
                <KycStatusModal title={isKYCRejected ? "Submission Failed" : "Submitted"}
                    type={2}
                    image={isKYCRejected ? require("../../Assets/Images/KYC/kyc-failure.png") : require("../../Assets/Images/KYC/kyc-success.webp")}
                    buttonName={isKYCRejected ? "Retry now" : "Understood"}
                    description={
                        isKYCRejected ?
                            "We couldn’t complete your account creation process due to a verification or system issue. Please review your details or try submitting again."
                            :
                            "The account creation process has been initiated and the process will take 1-3 business days time. We will notify over mail once it is completed."
                    }
                    onCloseModal={() => setStatusModal(false)}
                    onButtonClick={_handleModalClose}
                />
            }

        </section>
    )
}
export default SignUp;